import { useState } from 'react';
import { Alert, Button, Checkbox, FormControlLabel, Popover, TextField } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import type { FakesMapGroup } from '../../../api/src/fakes/fakes-matrix.types';
import { Box, Stack, Typography } from './mui-system';
import { SerpGroupLabel } from './SerpGroupLabel';

export function SerpGroupFilter({
  groups,
  value,
  disabled,
  saving,
  error,
  onChange,
}: {
  groups: FakesMapGroup[];
  value: string[] | null;
  disabled: boolean;
  saving: boolean;
  error: string;
  onChange(ids: string[] | null): void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const selected = new Set(value ?? groups.map((group) => group.id));
  const matches = groups.filter((group) =>
    group.name.toLocaleLowerCase('ru-RU').includes(search.trim().toLocaleLowerCase('ru-RU')),
  );
  const count = groups.filter((group) => selected.has(group.id)).length;
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<ExpandMore />}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        onClick={(event) => {
          setAnchor(event.currentTarget);
          setSearch('');
        }}
      >
        Группы на карте:{' '}
        {value === null ? `все (${groups.length})` : `${count} из ${groups.length}`}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          role="dialog"
          aria-label="Группы на карте"
          sx={{ p: 2, width: 380, maxWidth: 'calc(100vw - 32px)' }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Поиск групп"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Stack direction="row" gap={1} alignItems="center" my={1} flexWrap="wrap">
            <Button size="small" disabled={saving} onClick={() => onChange(null)}>
              Выбрать все
            </Button>
            <Button size="small" disabled={saving} onClick={() => onChange([])}>
              Снять выбор
            </Button>
            {saving && (
              <Typography role="status" fontSize={12}>
                Сохраняем…
              </Typography>
            )}
          </Stack>
          <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {matches.map((group) => (
              <FormControlLabel
                key={group.id}
                sx={{
                  display: 'flex',
                  m: 0,
                  py: 0.25,
                  '& .MuiFormControlLabel-label': { minWidth: 0 },
                }}
                control={
                  <Checkbox
                    checked={selected.has(group.id)}
                    disabled={saving}
                    onChange={(_, checked) =>
                      onChange(
                        checked
                          ? [...selected, group.id]
                          : [...selected].filter((id) => id !== group.id),
                      )
                    }
                    slotProps={{ input: { 'aria-label': group.name } }}
                  />
                }
                label={
                  <Stack gap={0.25}>
                    <SerpGroupLabel name={group.name} profile={group.profile} />
                    <Typography fontSize={11} color="text.secondary">
                      {group.queryCount ? `${group.queryCount} запросов` : 'Нет запросов в срезе'}
                    </Typography>
                  </Stack>
                }
              />
            ))}
            {!matches.length && (
              <Typography color="text.secondary" py={2}>
                Группы не найдены.
              </Typography>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
